import React, { useState, useEffect } from 'react';
import { Syringe, Calendar, AlertTriangle, CheckCircle, Clock, Plus, Edit2, Trash2, RefreshCw, Bell, BellOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const STATUS_CONFIG = {
  a_jour: { label: 'À jour', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', icon: CheckCircle },
  bientot_du: { label: 'Rappel proche', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', icon: Clock },
  en_retard: { label: 'En retard', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', icon: AlertTriangle }
};

// Species emoji
const SPECIES_EMOJI = {
  'chien': '🐕',
  'chat': '🐱',
  'lapin': '🐰',
  'oiseau': '🐦',
  'rongeur': '🐹',
  'autre': '🐾'
};

const VaccinationReminders = ({ token, animalId, animalName, animalSpecies }) => {
  const [reminders, setReminders] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [preferences, setPreferences] = useState(null);

  // Form state for adding/editing
  const [formData, setFormData] = useState({
    vaccine_code: '',
    vaccine_name: '',
    vaccination_date: new Date().toISOString().split('T')[0],
    next_due_date: ''
  });

  useEffect(() => {
    if (animalId) {
      fetchReminders();
      fetchProtocols();
      fetchPreferences();
    }
  }, [animalId]);

  const fetchReminders = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/vaccinations/animals/${animalId}/reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReminders(data);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
    setIsLoading(false);
  };

  const fetchProtocols = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vaccinations/protocols?species=${animalSpecies}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProtocols(data);
      }
    } catch (error) {
      console.error('Error fetching protocols:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vaccinations/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const handleSelectProtocol = (protocol) => {
    const vaccDate = new Date(formData.vaccination_date);
    const nextDate = new Date(vaccDate);
    nextDate.setDate(nextDate.getDate() + protocol.reminder_days);
    
    setFormData({
      ...formData,
      vaccine_code: protocol.vaccine_code,
      vaccine_name: protocol.vaccine_name,
      next_due_date: nextDate.toISOString().split('T')[0]
    });
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    
    if (!formData.vaccine_name || !formData.vaccination_date || !formData.next_due_date) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/vaccinations/animals/${animalId}/reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          animal_id: animalId,
          vaccine_name: formData.vaccine_name,
          vaccine_code: formData.vaccine_code || 'custom',
          vaccination_date: formData.vaccination_date,
          next_due_date: formData.next_due_date
        })
      });

      if (response.ok) {
        toast.success('Rappel vaccin créé');
        setShowAddModal(false);
        fetchReminders();
        setFormData({
          vaccine_code: '',
          vaccine_name: '',
          vaccination_date: new Date().toISOString().split('T')[0],
          next_due_date: ''
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleUpdateReminder = async (reminderId, newDate) => {
    try {
      const response = await fetch(`${API_URL}/api/vaccinations/animals/${animalId}/reminders/${reminderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ next_due_date: newDate })
      });

      if (response.ok) {
        toast.success('Date de rappel modifiée');
        setEditingReminder(null);
        fetchReminders();
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Supprimer ce rappel de vaccination ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/vaccinations/animals/${animalId}/reminders/${reminderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Rappel supprimé');
        fetchReminders();
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleToggleReminders = async () => {
    try {
      const newPrefs = { ...preferences, reminders_enabled: !preferences?.reminders_enabled };
      const response = await fetch(`${API_URL}/api/vaccinations/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPrefs)
      });

      if (response.ok) {
        setPreferences(newPrefs);
        toast.success(newPrefs.reminders_enabled ? 'Rappels activés' : 'Rappels désactivés');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <Syringe className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Vaccinations de {animalName}</h3>
            <p className="text-sm text-gray-500">{reminders.length} vaccin(s) enregistré(s)</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Toggle reminders */}
          <button
            onClick={handleToggleReminders}
            className={`p-2 rounded-lg transition-colors ${
              preferences?.reminders_enabled !== false
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
            title={preferences?.reminders_enabled !== false ? 'Rappels activés' : 'Rappels désactivés'}
          >
            {preferences?.reminders_enabled !== false ? (
              <Bell className="w-5 h-5" />
            ) : (
              <BellOff className="w-5 h-5" />
            )}
          </button>
          
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="add-vaccination-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un vaccin
          </Button>
        </div>
      </div>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <Syringe className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">Aucun vaccin enregistré</p>
          <p className="text-sm text-gray-400 mb-4">
            Ajoutez les vaccinations de {animalName} pour recevoir des rappels automatiques
          </p>
          <Button onClick={() => setShowAddModal(true)} variant="outline" className="border-purple-200 text-purple-600 hover:bg-purple-50">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un vaccin
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {reminders.map((reminder) => {
            const config = STATUS_CONFIG[reminder.status] || STATUS_CONFIG.a_jour;
            const StatusIcon = config.icon;
            
            return (
              <div 
                key={reminder.reminder_id}
                className={`p-4 rounded-xl ${config.bgLight} border border-gray-100`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* Status indicator */}
                    <div className={`p-2 ${config.color} rounded-lg text-white flex-shrink-0`}>
                      <Syringe className="w-5 h-5" />
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-800">{reminder.vaccine_name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bgLight} ${config.textColor} border border-current/20`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {config.label}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-600">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          Vacciné le : {formatDate(reminder.vaccination_date)}
                        </p>
                        
                        {editingReminder === reminder.reminder_id ? (
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="date"
                              defaultValue={reminder.next_due_date.split('T')[0]}
                              className="px-2 py-1 border rounded text-sm"
                              id={`edit-date-${reminder.reminder_id}`}
                            />
                            <button
                              onClick={() => {
                                const input = document.getElementById(`edit-date-${reminder.reminder_id}`);
                                handleUpdateReminder(reminder.reminder_id, input.value);
                              }}
                              className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                            >
                              Valider
                            </button>
                            <button
                              onClick={() => setEditingReminder(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <p className={`font-medium ${config.textColor}`}>
                            <Clock className="w-4 h-4 inline mr-1" />
                            Prochain rappel : {formatDate(reminder.next_due_date)}
                            {reminder.days_until_due !== null && (
                              <span className="ml-2 text-gray-500">
                                ({reminder.days_until_due > 0 
                                  ? `dans ${reminder.days_until_due} jours` 
                                  : `${Math.abs(reminder.days_until_due)} jours de retard`})
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingReminder(reminder.reminder_id)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Modifier la date"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteReminder(reminder.reminder_id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Vaccination Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Syringe className="w-5 h-5 text-purple-600" />
                Ajouter une vaccination
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Sélectionnez un vaccin pour créer un rappel automatique
              </p>
            </div>
            
            <form onSubmit={handleCreateReminder} className="p-6 space-y-4">
              {/* Protocol selection */}
              <div>
                <Label>Type de vaccin</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {protocols.map((protocol) => (
                    <button
                      key={protocol.vaccine_code}
                      type="button"
                      onClick={() => handleSelectProtocol(protocol)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.vaccine_code === protocol.vaccine_code
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-sm text-gray-800">{protocol.vaccine_name}</p>
                      <p className="text-xs text-gray-500">
                        Rappel: {Math.round(protocol.reminder_days / 30)} mois
                      </p>
                    </button>
                  ))}
                  {/* Custom option */}
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, vaccine_code: 'custom', vaccine_name: ''})}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.vaccine_code === 'custom'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-sm text-gray-800">Autre vaccin</p>
                    <p className="text-xs text-gray-500">Personnalisé</p>
                  </button>
                </div>
              </div>

              {/* Custom vaccine name */}
              {formData.vaccine_code === 'custom' && (
                <div>
                  <Label htmlFor="vaccine_name">Nom du vaccin *</Label>
                  <Input
                    id="vaccine_name"
                    value={formData.vaccine_name}
                    onChange={(e) => setFormData({...formData, vaccine_name: e.target.value})}
                    placeholder="Ex: Vaccin grippe équine"
                    className="mt-1"
                  />
                </div>
              )}

              {/* Vaccination date */}
              <div>
                <Label htmlFor="vaccination_date">Date de vaccination *</Label>
                <Input
                  id="vaccination_date"
                  type="date"
                  value={formData.vaccination_date}
                  onChange={(e) => {
                    setFormData({...formData, vaccination_date: e.target.value});
                    // Auto-calculate next date if protocol selected
                    if (formData.vaccine_code && formData.vaccine_code !== 'custom') {
                      const protocol = protocols.find(p => p.vaccine_code === formData.vaccine_code);
                      if (protocol) {
                        const vaccDate = new Date(e.target.value);
                        vaccDate.setDate(vaccDate.getDate() + protocol.reminder_days);
                        setFormData(prev => ({...prev, vaccination_date: e.target.value, next_due_date: vaccDate.toISOString().split('T')[0]}));
                      }
                    }
                  }}
                  className="mt-1"
                />
              </div>

              {/* Next due date */}
              <div>
                <Label htmlFor="next_due_date">Date du prochain rappel *</Label>
                <Input
                  id="next_due_date"
                  type="date"
                  value={formData.next_due_date}
                  onChange={(e) => setFormData({...formData, next_due_date: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vous pouvez modifier cette date si nécessaire
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="submit-vaccination-btn"
                >
                  <Syringe className="w-4 h-4 mr-2" />
                  Ajouter le vaccin
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaccinationReminders;
